import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, Check, X, User } from 'lucide-react';

/**
 * Searchable Account Select Dropdown
 * Allows selecting an account by searching client_id or account name.
 */
export default function AccountSelectDropdown({
    accounts = [],
    value = '',
    onChange,
    placeholder = 'Select Account...',
    label = '',
    badgeColor = 'blue',
    disabled = false,
    compact = false,
    className = ''
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Selected account object
    const selectedAccount = useMemo(() => {
        if (!value) return null;
        return accounts.find(
            a => String(a.client_id || a.clientId || '').trim().toLowerCase() === String(value).trim().toLowerCase()
        ) || { client_id: value, name: '' };
    }, [accounts, value]);

    // Filter accounts by search query
    const filteredAccounts = useMemo(() => {
        if (!searchTerm) return accounts;
        const q = searchTerm.toLowerCase().trim();
        return accounts.filter(acc => {
            const id = String(acc.client_id || acc.clientId || '').toLowerCase();
            const name = String(acc.name || acc.clientName || '').toLowerCase();
            return id.includes(q) || name.includes(q);
        });
    }, [accounts, searchTerm]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus input on open
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const handleSelect = (clientId) => {
        onChange(clientId);
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
    };

    const borderGlow = badgeColor === 'purple'
        ? 'focus-within:border-purple-500 focus-within:ring-1 focus-within:ring-purple-500/40 border-purple-500/40 hover:border-purple-400'
        : 'focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/40 border-blue-500/40 hover:border-blue-400';

    const pillBadgeClass = badgeColor === 'purple'
        ? 'bg-purple-500/15 text-purple-300 border-purple-500/30'
        : 'bg-blue-500/15 text-blue-300 border-blue-500/30';

    return (
        <div 
            className={`relative flex flex-col gap-1 ${compact ? 'min-w-[200px] max-w-[340px] w-full' : 'min-w-[220px]'} ${className}`} 
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
        >
            {label && !compact && (
                <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {label}
                    </span>
                    {selectedAccount && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${pillBadgeClass}`}>
                            {selectedAccount.client_id}
                        </span>
                    )}
                </div>
            )}

            {/* Trigger Button */}
            <button
                type="button"
                disabled={disabled}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) setIsOpen(prev => !prev);
                }}
                className={`flex items-center justify-between w-full ${
                    compact ? 'px-2.5 py-1 bg-[#10121a]/90 text-xs rounded-md shadow-sm' : 'px-3 py-2 bg-[#14161f] text-xs rounded-lg'
                } border text-left transition-all ${borderGlow} ${
                    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
            >
                <div className="flex items-center gap-1.5 truncate pr-1">
                    <User className={`w-3.5 h-3.5 shrink-0 ${badgeColor === 'purple' ? 'text-purple-400' : 'text-blue-400'}`} />
                    {selectedAccount ? (
                        <div className="truncate flex items-center gap-1">
                            <span className="font-semibold text-gray-100 font-mono">
                                {selectedAccount.client_id}
                            </span>
                            {selectedAccount.name && (
                                <span className="text-gray-400 text-[11px] truncate max-w-[140px]">
                                    ({selectedAccount.name})
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>

                <div className="flex items-center gap-1 shrink-0">
                    {selectedAccount && !disabled && (
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={handleClear}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e); }}
                            className="p-0.5 rounded hover:bg-gray-700/60 text-gray-400 hover:text-gray-200 transition-colors"
                            title="Clear selection"
                        >
                            <X className="w-3 h-3" />
                        </span>
                    )}
                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full left-0 mt-1.5 z-[70] min-w-[280px] w-full bg-[#1a1d29] border border-gray-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                    {/* Search Field */}
                    <div className="p-2 border-b border-gray-800 bg-[#14161f]">
                        <div className="relative flex items-center">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 text-gray-400 pointer-events-none" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search client ID or name..."
                                className="w-full pl-8 pr-7 py-1.5 bg-[#0f1116] border border-gray-700 rounded-md text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 text-gray-500 hover:text-gray-300"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Accounts List */}
                    <div className="max-h-56 overflow-y-auto custom-scrollbar divide-y divide-gray-800/40 p-1">
                        {filteredAccounts.length === 0 ? (
                            <div className="py-6 px-3 text-center text-xs text-gray-500">
                                No accounts found matching "{searchTerm}"
                            </div>
                        ) : (
                            filteredAccounts.map((acc) => {
                                const accId = acc.client_id || acc.clientId;
                                const accName = acc.name || acc.clientName || '';
                                const isSelected = String(accId).trim().toLowerCase() === String(value).trim().toLowerCase();

                                return (
                                    <button
                                        key={accId}
                                        type="button"
                                        onClick={() => handleSelect(accId)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center justify-between transition-colors ${
                                            isSelected
                                                ? badgeColor === 'purple'
                                                    ? 'bg-purple-600/20 text-purple-200 font-semibold'
                                                    : 'bg-blue-600/20 text-blue-200 font-semibold'
                                                : 'hover:bg-gray-800/70 text-gray-300 hover:text-white'
                                        }`}
                                    >
                                        <div className="flex flex-col truncate pr-2">
                                            <div className="flex items-center gap-1.5 font-mono">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
                                                <span>{accId}</span>
                                            </div>
                                            {accName && (
                                                <span className="text-[11px] text-gray-400 font-normal truncate pl-3">
                                                    {accName}
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <Check className={`w-3.5 h-3.5 shrink-0 ${badgeColor === 'purple' ? 'text-purple-400' : 'text-blue-400'}`} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* Footer count */}
                    <div className="px-3 py-1.5 bg-[#14161f] border-t border-gray-800 text-[10px] text-gray-500 flex justify-between">
                        <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                            {filteredAccounts.length} Active Accounts
                        </span>
                        {value && (
                            <button
                                onClick={handleClear}
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                Clear Selection
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
